import { useState, useEffect } from "react";
import { useAuth } from "../lib/AuthContext";
import {
  Eye, EyeOff, Check, AlertCircle, Users, FileText,
  BarChart2, Key, Download
} from "lucide-react";

// ─── Constants ────────────────────────────────────────────────────────────────

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

// ─── Profile Section ──────────────────────────────────────────────────────────

function ProfileSection({ user: authUser }) {
  const [user, setUser] = useState(authUser);
  const [form, setForm] = useState({
    full_name: authUser?.full_name ?? "",
    email:     authUser?.email ?? "",
    phone:     authUser?.phone ?? "",
    title:     authUser?.title ?? "",
  });
  const [saving, setSaving] = useState(false);
  const [saved,  setSaved]  = useState(false);
  const [error,  setError]  = useState("");

  useEffect(() => {
    fetch("/api/me", {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    })
      .then(res => res.json())
      .then(data => {
        if (!data.error) {
          setUser(data);
          setForm({
            full_name: data.full_name ?? "",
            email:     data.email ?? "",
            phone:     data.phone ?? "",
            title:     data.title ?? "",
          });
        }
      })
      .catch(() => {});
  }, []);

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true); setError(""); setSaved(false);
    try {
      const res = await fetch("/api/users", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        credentials: "include",
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.error || "Failed to save");
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="card-header"><span className="card-title">👤 My Profile</span></div>
      <div className="card-body">
        <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 24 }}>
          <div style={{
            width: 56, height: 56, borderRadius: "50%",
            background: "linear-gradient(135deg, #0f172a, #334155)",
            color: "#fff", fontSize: 22, fontWeight: 700,
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}>
            {(user?.full_name?.[0] ?? user?.email?.[0] ?? "?").toUpperCase()}
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color: "#0f172a" }}>{user?.full_name ?? "—"}</div>
            <div style={{ fontSize: 13, color: "#64748b" }}>{user?.email ?? "—"}</div>
          </div>
        </div>

        <form onSubmit={handleSave}>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Full Name</label>
              <input className="form-input" value={form.full_name}
                onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} placeholder="Jane Smith" />
            </div>
            <div className="form-group">
              <label className="form-label">Email (Login)</label>
              <input className="form-input" type="email" value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="jane@company.com" />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Phone Number</label>
              <input className="form-input" value={form.phone}
                onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+1 (555) 000-0000" />
            </div>
            <div className="form-group">
              <label className="form-label">Job Title</label>
              <input className="form-input" value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Sales Representative" />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Role</label>
              <div style={{ paddingTop: 6 }}>
                <span className={`badge badge-${user?.role ?? "sales_rep"}`}>
                  {(user?.role ?? "sales_rep").replace("_", " ")}
                </span>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Date Joined</label>
              <input className="form-input" disabled
                value={formatDate(user?.registered_at ?? user?.created_at)} />
            </div>
          </div>

          {error && <p className="settings-error"><AlertCircle size={14} />{error}</p>}
          {saved && <p className="settings-success"><Check size={14} />Profile updated successfully</p>}

          <button type="submit" className="btn btn-primary" disabled={saving} style={{ marginTop: 8 }}>
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </form>
      </div>
    </div>
  );
}

// ─── Password Section ─────────────────────────────────────────────────────────

function PasswordSection() {
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [show, setShow] = useState({ current: false, next: false, confirm: false });
  const [saving, setSaving] = useState(false);
  const [saved,  setSaved]  = useState(false);
  const [error,  setError]  = useState("");

  const strength = (() => {
    const p = form.next;
    if (!p) return 0;
    let s = 0;
    if (p.length >= 8)          s++;
    if (/[A-Z]/.test(p))        s++;
    if (/[0-9]/.test(p))        s++;
    if (/[^A-Za-z0-9]/.test(p)) s++;
    return s;
  })();

  const strengthLabel = ["", "Weak", "Fair", "Good", "Strong"][strength];
  const strengthColor = ["", "#ef4444", "#f59e0b", "#10b981", "#10b981"][strength];

  async function handleChange(e) {
    e.preventDefault();
    setError(""); setSaved(false);
    if (form.next !== form.confirm) return setError("Passwords do not match");
    if (form.next.length < 8)      return setError("Password must be at least 8 characters");
    setSaving(true);
    try {
      const res = await fetch("/api/changepassword", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        credentials: "include",
        body: JSON.stringify({ currentPassword: form.current, newPassword: form.next }),
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.error || "Failed to change password");
      }
      setSaved(true);
      setForm({ current: "", next: "", confirm: "" });
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  const toggle = k => setShow(s => ({ ...s, [k]: !s[k] }));

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="card-header"><span className="card-title">🔒 Change Password</span></div>
      <div className="card-body">
        <form onSubmit={handleChange}>
          {["current", "next", "confirm"].map(k => (
            <div key={k} className="form-group">
              <label className="form-label">
                {k === "current" ? "Current Password" : k === "next" ? "New Password" : "Confirm New Password"}
              </label>
              <div className="settings-input-icon">
                <input
                  className="form-input"
                  type={show[k] ? "text" : "password"}
                  value={form[k]}
                  onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))}
                  placeholder="••••••••"
                  autoComplete={k === "current" ? "current-password" : "new-password"}
                />
                <button type="button" className="settings-eye-btn" onClick={() => toggle(k)}>
                  {show[k] ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              {k === "next" && form.next && (
                <div className="settings-strength" style={{ marginTop: 6 }}>
                  <div className="settings-strength-bar">
                    {[1, 2, 3, 4].map(i => (
                      <div key={i} className="settings-strength-seg"
                        style={{ background: i <= strength ? strengthColor : "#e2e8f0" }} />
                    ))}
                  </div>
                  <span style={{ color: strengthColor, fontSize: 12 }}>{strengthLabel}</span>
                </div>
              )}
            </div>
          ))}

          {error && <p className="settings-error"><AlertCircle size={14} />{error}</p>}
          {saved && <p className="settings-success"><Check size={14} />Password changed successfully</p>}

          <button type="submit" className="btn btn-primary"
            disabled={saving || !form.current || !form.next || !form.confirm}
            style={{ marginTop: 8 }}>
            {saving ? "Updating…" : "Update Password"}
          </button>
        </form>
      </div>
    </div>
  );
}

// ─── Integrations Section (admin only, read-only status) ─────────────────────

function IntegrationsSection() {
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetch("/api/integrations", {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    })
      .then(res => res.json())
      .then(data => {
        if (data.error) throw new Error(data.error);
        setIntegrations(data.integrations ?? []);
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="card-header">
        <span className="card-title">🔑 Integrations</span>
        <span className="settings-badge">Admin Only</span>
      </div>
      <div className="card-body">
        <p style={{ fontSize: 13, color: "#64748b", marginBottom: 16 }}>
          Integration secrets are configured as environment variables in your Cloudflare Worker.
          Manage them via the{" "}
          <a href="https://dash.cloudflare.com" target="_blank" rel="noreferrer"
            style={{ color: "#2563eb", textDecoration: "none" }}>
            Cloudflare dashboard
          </a>{" "}
          or <code style={{ fontSize: 12, background: "#f1f5f9", padding: "1px 5px", borderRadius: 4 }}>wrangler secret put</code>.
        </p>

        {loading && <p style={{ fontSize: 13, color: "#94a3b8" }}>Checking configuration…</p>}
        {error && <p className="settings-error"><AlertCircle size={14} />{error}</p>}

        {!loading && !error && (
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {integrations.map(({ group, keys }) => {
              const allConfigured = keys.every(k => k.configured);
              const someConfigured = keys.some(k => k.configured);
              const groupStatus = allConfigured ? "ok" : someConfigured ? "partial" : "missing";
              const groupLabel = allConfigured ? "✓ All configured" : someConfigured ? "⚠ Partial" : "✗ Not set";
              return (
                <div key={group}>
                  {/* Group header */}
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>{group}</span>
                    <span className={`integration-status-badge ${groupStatus}`}>{groupLabel}</span>
                  </div>
                  {/* Keys */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 0, background: "#f8fafc", borderRadius: 8, border: "1px solid #e2e8f0", overflow: "hidden" }}>
                    {keys.map(({ key, label, configured }) => (
                      <div key={key} className="integration-status-row" style={{ padding: "9px 14px" }}>
                        <div style={{ flex: 1 }}>
                          <span style={{ fontSize: 13, color: "#475569" }}>{label}</span>
                          <code style={{ fontSize: 11, color: "#94a3b8", marginLeft: 8 }}>{key}</code>
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 600, color: configured ? "#16a34a" : "#dc2626" }}>
                          {configured ? "✓" : "✗"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Admin: User Management Panel ────────────────────────────────────────────

function UserManagementPanel({ currentUserId }) {
  const [members, setMembers]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [actionUser, setActionUser] = useState(null); // user being acted on
  const [modal, setModal]           = useState(null);  // "role" | "password" | "delete"
  const [newRole, setNewRole]       = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showPw, setShowPw]         = useState(false);
  const [working, setWorking]       = useState(false);
  const [feedback, setFeedback]     = useState({ text: "", ok: true });

  const token = () => localStorage.getItem("token");

  useEffect(() => { loadMembers(); }, []);

  async function loadMembers() {
    setLoading(true);
    try {
      const res = await fetch("/api/users", { headers: { Authorization: `Bearer ${token()}` } });
      const data = await res.json();
      setMembers(Array.isArray(data) ? data : []);
    } catch { setMembers([]); }
    setLoading(false);
  }

  function openModal(user, type) {
    setActionUser(user);
    setModal(type);
    setNewRole(user.role ?? "sales_rep");
    setNewPassword("");
    setShowPw(false);
    setFeedback({ text: "", ok: true });
  }

  function closeModal() { setModal(null); setActionUser(null); setWorking(false); }

  async function patchUser(payload) {
    const res = await fetch("/api/users", {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` },
      body: JSON.stringify({ targetId: actionUser.id, ...payload }),
    });
    const d = await res.json();
    if (!res.ok) throw new Error(d.error || "Request failed");
    return d;
  }

  async function handleRoleSave() {
    setWorking(true);
    try {
      await patchUser({ role: newRole });
      setFeedback({ text: "Role updated.", ok: true });
      await loadMembers();
      setTimeout(closeModal, 1200);
    } catch (e) { setFeedback({ text: e.message, ok: false }); }
    setWorking(false);
  }

  async function handlePasswordSave() {
    if (newPassword.length < 8) { setFeedback({ text: "Min 8 characters.", ok: false }); return; }
    setWorking(true);
    try {
      await patchUser({ new_password: newPassword });
      setFeedback({ text: "Password updated.", ok: true });
      setTimeout(closeModal, 1200);
    } catch (e) { setFeedback({ text: e.message, ok: false }); }
    setWorking(false);
  }

  async function handleDelete() {
    setWorking(true);
    try {
      const res = await fetch(`/api/users?id=${actionUser.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token()}` },
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "Delete failed");
      await loadMembers();
      closeModal();
    } catch (e) { setFeedback({ text: e.message, ok: false }); }
    setWorking(false);
  }

  const roleBadgeStyle = (role) => role === "admin"
    ? { background: "#eff6ff", color: "#2563eb" }
    : { background: "#f0fdf4", color: "#16a34a" };

  if (loading) return <p style={{ fontSize: 13, color: "#64748b", padding: "12px 0" }}>Loading users…</p>;

  return (
    <div className="settings-admin-panel">
      <p className="settings-admin-desc">
        Manage roles, reset passwords, and remove team members.
      </p>

      {/* ── User rows ── */}
      <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
        {members.map(m => {
          const isSelf = m.id === currentUserId;
          return (
            <div key={m.id} className="admin-user-row">
              {/* Avatar + info */}
              <div className="crm-avatar" style={{ width: 34, height: 34, fontSize: 13, flexShrink: 0 }}>
                {(m.full_name?.[0] ?? m.email?.[0] ?? "?").toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13.5, color: "#0f172a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {m.full_name ?? "—"} {isSelf && <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 400 }}>(you)</span>}
                </div>
                <div style={{ fontSize: 12, color: "#64748b" }}>{m.email}</div>
              </div>

              {/* Role badge */}
              <span style={{ ...roleBadgeStyle(m.role), fontSize: 11, fontWeight: 600, padding: "2px 9px", borderRadius: 20, whiteSpace: "nowrap" }}>
                {(m.role ?? "sales_rep").replace("_", " ")}
              </span>

              {/* Action buttons — disabled for self */}
              {!isSelf && (
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  <button
                    className="btn btn-secondary btn-sm"
                    title="Change role"
                    onClick={() => openModal(m, "role")}
                  >
                    Change Role
                  </button>
                  <button
                    className="btn btn-secondary btn-sm"
                    title="Force reset password"
                    onClick={() => openModal(m, "password")}
                  >
                    Reset Password
                  </button>
                  <button
                    className="btn btn-sm"
                    style={{ background: "#fee2e2", color: "#dc2626", border: "none" }}
                    title="Delete user"
                    onClick={() => openModal(m, "delete")}
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>
          );
        })}
        {members.length === 0 && <p style={{ fontSize: 13, color: "#64748b" }}>No users found.</p>}
      </div>

      {/* ── Modal ── */}
      {modal && (
        <>
          <div className="drawer-overlay" onClick={closeModal} />
          <div className="admin-modal">
            {/* Role modal */}
            {modal === "role" && (
              <>
                <div className="admin-modal-header">
                  <span className="drawer-title">Change Role</span>
                  <button className="btn-ghost btn btn-icon" onClick={closeModal}>✕</button>
                </div>
                <div className="admin-modal-body">
                  <p style={{ fontSize: 13, color: "#64748b", marginBottom: 14 }}>
                    Changing role for <strong>{actionUser.full_name ?? actionUser.email}</strong>.
                  </p>
                  <div className="form-group">
                    <label className="form-label">Role</label>
                    <select className="form-select" value={newRole} onChange={e => setNewRole(e.target.value)}>
                      <option value="sales_rep">Sales Rep</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  {feedback.text && (
                    <p style={{ fontSize: 13, color: feedback.ok ? "#16a34a" : "#dc2626", margin: "4px 0 8px" }}>
                      {feedback.text}
                    </p>
                  )}
                  <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                    <button className="btn btn-primary" disabled={working} onClick={handleRoleSave}>
                      {working ? "Saving…" : "Save Role"}
                    </button>
                    <button className="btn btn-secondary" onClick={closeModal}>Cancel</button>
                  </div>
                </div>
              </>
            )}

            {/* Password modal */}
            {modal === "password" && (
              <>
                <div className="admin-modal-header">
                  <span className="drawer-title">Force Reset Password</span>
                  <button className="btn-ghost btn btn-icon" onClick={closeModal}>✕</button>
                </div>
                <div className="admin-modal-body">
                  <p style={{ fontSize: 13, color: "#64748b", marginBottom: 14 }}>
                    Set a new password for <strong>{actionUser.full_name ?? actionUser.email}</strong>. Share it with them securely.
                  </p>
                  <div className="form-group">
                    <label className="form-label">New Password</label>
                    <div className="settings-input-icon">
                      <input
                        className="form-input"
                        type={showPw ? "text" : "password"}
                        value={newPassword}
                        onChange={e => setNewPassword(e.target.value)}
                        placeholder="Min 8 characters"
                      />
                      <button type="button" className="settings-eye-btn" onClick={() => setShowPw(s => !s)}>
                        {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                      </button>
                    </div>
                  </div>
                  {feedback.text && (
                    <p style={{ fontSize: 13, color: feedback.ok ? "#16a34a" : "#dc2626", margin: "4px 0 8px" }}>
                      {feedback.text}
                    </p>
                  )}
                  <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                    <button className="btn btn-primary" disabled={working} onClick={handlePasswordSave}>
                      {working ? "Updating…" : "Set Password"}
                    </button>
                    <button className="btn btn-secondary" onClick={closeModal}>Cancel</button>
                  </div>
                </div>
              </>
            )}

            {/* Delete modal */}
            {modal === "delete" && (
              <>
                <div className="admin-modal-header">
                  <span className="drawer-title" style={{ color: "#dc2626" }}>Delete User</span>
                  <button className="btn-ghost btn btn-icon" onClick={closeModal}>✕</button>
                </div>
                <div className="admin-modal-body">
                  <p style={{ fontSize: 13, color: "#475569", marginBottom: 14 }}>
                    Permanently delete <strong>{actionUser.full_name ?? actionUser.email}</strong>?
                    This cannot be undone and will remove all their data.
                  </p>
                  {feedback.text && (
                    <p style={{ fontSize: 13, color: "#dc2626", margin: "4px 0 8px" }}>{feedback.text}</p>
                  )}
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      className="btn"
                      style={{ background: "#ef4444", color: "#fff", border: "none" }}
                      disabled={working}
                      onClick={handleDelete}
                    >
                      {working ? "Deleting…" : "Yes, Delete"}
                    </button>
                    <button className="btn btn-secondary" onClick={closeModal}>Cancel</button>
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Admin Controls Section ───────────────────────────────────────────────────

function AdminSection({ currentUserId }) {
  const [activeTab, setActiveTab] = useState("users");

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="card-header">
        <span className="card-title">🛡 Admin Controls</span>
        <span className="settings-badge">Admin Only</span>
      </div>
      <div className="card-body">
        <div className="settings-tabs">
          {[
            { key: "users",   label: "User Management", icon: Users },
            { key: "api",     label: "API Keys",         icon: Key },
            { key: "audit",   label: "Audit Log",        icon: FileText },
            { key: "reports", label: "Reports",          icon: BarChart2 },
          ].map(({ key, label, icon: Icon }) => (
            <button key={key}
              className={`settings-tab ${activeTab === key ? "active" : ""}`}
              onClick={() => setActiveTab(key)}>
              <Icon size={14} />{label}
            </button>
          ))}
        </div>

        {activeTab === "users" && <UserManagementPanel currentUserId={currentUserId} />}

        {activeTab === "api" && (
          <div className="settings-admin-panel">
            <p className="settings-admin-desc">Manage API keys for integrations.</p>
            <div className="settings-api-key">
              <code>sk-••••••••••••••••••••••••••••••••</code>
              <button className="settings-btn-ghost"><Eye size={14} /></button>
              <button className="settings-btn-ghost"><Upload size={14} /></button>
            </div>
            <button className="btn btn-secondary" style={{ marginTop: 8 }}>
              <Key size={15} /> Regenerate API Key
            </button>
          </div>
        )}

        {activeTab === "audit" && (
          <div className="settings-admin-panel">
            <p className="settings-admin-desc">Recent system activity.</p>
            {[
              { action: "User invited",     user: "admin@company.com", time: "2 min ago" },
              { action: "Password changed", user: "rep@company.com",   time: "1 hr ago"  },
              { action: "Lead assigned",    user: "admin@company.com", time: "3 hr ago"  },
            ].map((item, i) => (
              <div key={i} className="settings-audit-row">
                <div className="settings-audit-dot" />
                <div>
                  <p className="settings-audit-action">{item.action}</p>
                  <p className="settings-audit-meta">{item.user} · {item.time}</p>
                </div>
              </div>
            ))}
            <button className="settings-btn-ghost" style={{ marginTop: 8 }}>View full audit log →</button>
          </div>
        )}

        {activeTab === "reports" && (
          <div className="settings-admin-panel">
            <p className="settings-admin-desc">Download performance and compliance reports.</p>
            {["Monthly Sales Report", "Team Performance Summary", "Contractor Tax Summary"].map(r => (
              <div key={r} className="settings-report-row">
                <FileText size={15} style={{ color: "#64748b" }} />
                <span>{r}</span>
                <button className="settings-btn-ghost" style={{ marginLeft: "auto" }}><Download size={14} /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Sales Rep: Onboarding / Documents ───────────────────────────────────────
// Sales reps only see: password + this onboarding section (no integrations, no admin panel)

function OnboardingSection({ user }) {
  const docs = [
    { label: "Signed Contract", key: "contract", status: user?.onboarding?.contract_signed ? "signed" : "pending" },
    { label: "W-9 / Tax Form",  key: "tax_form", status: user?.onboarding?.tax_form_signed  ? "signed" : "pending" },
  ];

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="card-header"><span className="card-title">📄 Onboarding &amp; Documents</span></div>
      <div className="card-body">
        <div className="settings-docs-list">
          {docs.map(doc => (
            <div key={doc.key} className="settings-doc-row">
              <FileText size={18} style={{ color: "#64748b", flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <p className="settings-doc-label">{doc.label}</p>
                <p className={`settings-doc-status ${doc.status}`}>
                  {doc.status === "signed" ? "✓ Signed" : "⚠ Signature required"}
                </p>
              </div>
              <button className="settings-btn-ghost">
                {doc.status === "signed" ? <Download size={14} /> : <span style={{fontSize:18,lineHeight:1}}>›</span>}
              </button>
            </div>
          ))}
        </div>
        {docs.some(d => d.status === "pending") && (
          <button className="btn btn-primary" style={{ marginTop: 12 }}
            onClick={() => window.location.href = "/onboarding"}>
            Complete Onboarding →
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Danger Zone ──────────────────────────────────────────────────────────────

function DangerZone() {
  const [confirm, setConfirm] = useState(false);
  const [typed,   setTyped]   = useState("");

  return (
    <div className="card" style={{ marginBottom: 20, borderColor: "#fecaca" }}>
      <div className="card-header">
        <span className="card-title" style={{ color: "#ef4444" }}>⚠ Danger Zone</span>
      </div>
      <div className="card-body">
        <p style={{ color: "#64748b", fontSize: 14, margin: "0 0 16px" }}>
          Permanently delete your account and all associated data. This cannot be undone.
        </p>
        {!confirm ? (
          <button className="btn"
            style={{ background: "#ef4444", color: "#fff", border: "none" }}
            onClick={() => setConfirm(true)}>
            Delete My Account
          </button>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <p style={{ fontSize: 14, color: "#475569", margin: 0 }}>
              Type <strong>DELETE</strong> to confirm.
            </p>
            <input className="form-input" style={{ maxWidth: 200, borderColor: "#fecaca" }}
              placeholder="Type DELETE" value={typed} onChange={e => setTyped(e.target.value)} />
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn"
                style={{ background: "#ef4444", color: "#fff", border: "none", opacity: typed !== "DELETE" ? 0.5 : 1 }}
                disabled={typed !== "DELETE"}>
                Confirm Delete
              </button>
              <button className="btn btn-secondary" onClick={() => { setConfirm(false); setTyped(""); }}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Section configs per role ─────────────────────────────────────────────────
//
// Access tiers:
//   admin     → Profile, Password, Integrations, Admin Controls, Danger Zone
//   sales_rep → Profile, Password, Onboarding, Danger Zone
//
// The `adminOnly` / `salesOnly` flags control visibility. Role checking happens
// here in Settings, not in Layout, so the route itself stays accessible to all
// authenticated users and the component decides what to render.

const ADMIN_SECTIONS = [
  { key: "profile",      label: "Profile",       emoji: "👤" },
  { key: "password",     label: "Password",      emoji: "🔒" },
  { key: "integrations", label: "Integrations",  emoji: "🔑" },
  { key: "admin",        label: "Admin Controls", emoji: "🛡" },
  { key: "danger",       label: "Danger Zone",   emoji: "⚠",  danger: true },
];

const SALES_SECTIONS = [
  { key: "profile",    label: "Profile",    emoji: "👤" },
  { key: "password",   label: "Password",   emoji: "🔒" },
  { key: "onboarding", label: "Onboarding", emoji: "📄" },
  { key: "danger",     label: "Danger Zone", emoji: "⚠", danger: true },
];

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const sections = isAdmin ? ADMIN_SECTIONS : SALES_SECTIONS;
  const [active, setActive] = useState("profile");

  const validKeys = sections.map(s => s.key);
  const safeActive = validKeys.includes(active) ? active : "profile";

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">Settings</div>
          <div className="page-subtitle">Manage your account and preferences.</div>
        </div>
      </div>

      {/* ── Top tab bar ── */}
      <div className="settings-tabs-bar">
        {sections.map(({ key, label, emoji, danger }) => (
          <button
            key={key}
            className={`settings-top-tab ${safeActive === key ? "active" : ""} ${danger ? "danger" : ""}`}
            onClick={() => setActive(key)}
          >
            <span>{emoji}</span>
            {label}
          </button>
        ))}
      </div>

      {/* ── Content ── */}
      <div className="settings-tab-content">
        {safeActive === "profile"      && <ProfileSection user={user} />}
        {safeActive === "password"     && <PasswordSection />}
        {safeActive === "integrations" && isAdmin && <IntegrationsSection />}
        {safeActive === "admin"        && isAdmin && <AdminSection currentUserId={user?.id} />}
        {safeActive === "onboarding"   && !isAdmin && <OnboardingSection user={user} />}
        {safeActive === "danger"       && <DangerZone />}
      </div>
    </div>
  );
}